import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { isNil, sortBy } from 'lodash';
import { interval, mergeMap, Observable, switchMap } from 'rxjs';
import { onlyAllowDistinctForDuration } from '../../../core/rxjs-pipes/only-allow-distinct-for-duration';
import { operationsToLoadStatus } from '../../../core/rxjs-pipes/operations-to-load-status';
import { ELoadStatus } from '../../../state/state.interface';
import { DateUtilitiesService } from '../../../utilities/date-utilities.service';
import { HttpUtilitiesService, IBaseResponse } from '../../../utilities/http-utilities.service';
import {
  IStepperDataResponse,
  IWorkInstructionStep,
  IWorkInstructionSubmission,
} from '../../settings/work-instructions/work-instruction-editor/work-instruction-steps/work-instruction-steps.interface';
import { ICheckIn, ICheckInUser } from '../items-modal/work-instruction-check-in/work-instruction-check-in.interface';
import {
  IItemAssignedStepCreateDto,
  IItemAssignedStepUpdateDto,
  ISubmissionUpsertDto,
} from './work-instruction-stepper.interface';
import { WorkInstructionStepperService } from './work-instruction-stepper.service';

export interface IWorkInstructionStepperState {
  assignmentLoading: ELoadStatus; // TODO bu kullaniliyor mu?
  totalProcessCycleTime: number | null;
  guideStartedAt: string | null;
  guideEndedAt: string | null;
  dataLoading: ELoadStatus;
  steps: IWorkInstructionStep[];
  submissions: IWorkInstructionSubmission[];
  ongoingUpserts: symbol[];
  totalTargetDuration: number | null;
  totalTimeToComplete: string | null;
  checkIns: ICheckIn[];
}

@Injectable()
export class WorkInstructionStepperStore extends ComponentStore<IWorkInstructionStepperState> {
  private readonly ticker = toSignal(interval(1000), { initialValue: 0 });

  public readonly steps: Signal<IWorkInstructionStep[]> = this.selectSignal(
    (state: IWorkInstructionStepperState) => state.steps,
  );
  public readonly submissions: Signal<IWorkInstructionSubmission[]> = this.selectSignal(
    (state: IWorkInstructionStepperState) => state.submissions,
  );
  public readonly totalTargetDuration: Signal<number | null> = this.selectSignal(
    (state: IWorkInstructionStepperState) => state.totalTargetDuration,
  );
  public readonly totalProcessCycleTime: Signal<number | null> = this.selectSignal(
    (state: IWorkInstructionStepperState) => state.totalProcessCycleTime,
  );
  public readonly actualDuration = computed(() => {
    this.ticker();

    const state = this.get();

    if (!state.guideStartedAt) return 0;

    return state.guideStartedAt ? DateUtilitiesService.getDiff(state.guideStartedAt, state.guideEndedAt) : 0;
  });

  public readonly totalTimeToComplete: Signal<string | null> = this.selectSignal(
    (state: IWorkInstructionStepperState) => state.totalTimeToComplete,
  );

  public readonly upsertStatus$ = this.select(({ ongoingUpserts }) => ongoingUpserts).pipe(operationsToLoadStatus());
  public readonly upsertStatus = toSignal(this.upsertStatus$, { initialValue: ELoadStatus.initial });
  public readonly dataLoading = this.selectSignal((state) => state.dataLoading);
  /** Ongoing check-ins on the work instruction and on its steps. */
  private readonly checkIns: Signal<ICheckIn[]> = this.selectSignal((state) => state.checkIns);
  /** Badge of the stepper's Check-ins button. */
  public readonly checkInCount: Signal<number> = computed(() => this.checkIns().length);
  /** Users checked in to each step, keyed by step id (steps without check-ins are absent). */
  public readonly stepCheckInUsers: Signal<Record<number, ICheckInUser[]>> = computed(() =>
    this.checkIns().reduce((usersByStepId: Record<number, ICheckInUser[]>, checkIn: ICheckIn) => {
      if (checkIn.guideStepId !== null && checkIn.user) {
        (usersByStepId[checkIn.guideStepId] ??= []).push(checkIn.user);
      }

      return usersByStepId;
    }, {}),
  );

  public readonly combinedStatus = computed(() => {
    return this.httpUtilities.combineHttpStatusesIntoOne([this.dataLoading(), this.upsertStatus()]);
  });
  public readonly submissions$ = this.select((state: IWorkInstructionStepperState) => state.submissions);

  readonly loadStepsAndSubmissions = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((itemWorkInstructionAssignmentId: number) => {
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getStepsAndSubmissions(itemWorkInstructionAssignmentId).pipe(
          tapResponse(
            (response: IBaseResponse<IStepperDataResponse>) =>
              this.patchState({ dataLoading: ELoadStatus.success, ...this.extractStepsAndSubmissions(response.data) }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly upsertWorkInstructionSubmission = this.effect((trigger$: Observable<ISubmissionUpsertDto>) =>
    trigger$.pipe(
      onlyAllowDistinctForDuration<ISubmissionUpsertDto>(),
      mergeMap(({ submissionId, ...params }) => {
        const upsertSymbol = Symbol();
        this.patchState(({ ongoingUpserts }) => ({ ongoingUpserts: [...ongoingUpserts, upsertSymbol] }));

        return (
          submissionId
            ? this.updateOne(submissionId, params.dto)
            : this.createOne({ ...params.dto, itemGuideAssignmentId: params.guideAssignmentId, stepId: params.stepId })
        ).pipe(
          tapResponse(
            () =>
              this.patchState(({ ongoingUpserts }) => ({
                ongoingUpserts: ongoingUpserts.filter((symbol) => symbol !== upsertSymbol),
              })),
            // eslint-disable-next-line no-console
            (error) => console.error('Error updating data', error),
          ),
        );
      }),
    ),
  );

  readonly loadCheckIns = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((itemGuideAssignmentId: number) =>
        this.service.getOngoingCheckIns(itemGuideAssignmentId).pipe(
          tapResponse(
            (response: IBaseResponse<ICheckIn[]>) => this.patchState({ checkIns: response.data }),
            // Badge and avatars are informative only; keep the last known check-ins on failure.
            () => undefined,
          ),
        ),
      ),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
    private readonly service: WorkInstructionStepperService,
  ) {
    super({
      assignmentLoading: ELoadStatus.initial,
      checkIns: [],
      dataLoading: ELoadStatus.initial,
      guideEndedAt: null,
      guideStartedAt: null,
      ongoingUpserts: [],
      steps: [],
      submissions: [],
      totalProcessCycleTime: null,
      totalTargetDuration: null,
      totalTimeToComplete: null,
    });
  }

  private extractStepsAndSubmissions(
    responseData: IStepperDataResponse,
  ): Pick<
    IWorkInstructionStepperState,
    | 'submissions'
    | 'steps'
    | 'totalTargetDuration'
    | 'totalTimeToComplete'
    | 'totalProcessCycleTime'
    | 'guideStartedAt'
    | 'guideEndedAt'
  > {
    let totalTargetDuration = 0;

    return {
      guideEndedAt: responseData.endedAt,
      guideStartedAt: responseData.startedAt,
      steps: sortBy(responseData.guide.guideSteps, 'order').map((step: IWorkInstructionStep, index: number) => {
        totalTargetDuration += isNil(step.targetDuration) ? 0 : step.targetDuration;

        return {
          ...step,
          index: index + 1,
          submission: responseData.guideSubmissions.find(
            (submission: IWorkInstructionSubmission) => submission.stepId === step.id,
          ),
          timeToComplete: isNil(step.targetDuration)
            ? null
            : DateUtilitiesService.formatSecondsToTimer(step.targetDuration),
        };
      }),
      submissions: responseData.guideSubmissions,
      totalProcessCycleTime: responseData.guide.totalProcessCycleTime,
      totalTargetDuration,
      totalTimeToComplete: DateUtilitiesService.formatSecondsToTimer(totalTargetDuration),
    };
  }

  private getStepsAndSubmissions(
    itemWorkInstructionAssignmentId: number,
  ): Observable<IBaseResponse<IStepperDataResponse>> {
    const httpParams: HttpParams = new HttpParams()
      .set('fields', 'boardId,startedAt,endedAt')
      .append('join', 'guide')
      .append('join', 'guide.guideSteps')
      .append('join', 'guideSubmissions');

    return this.http.get<IBaseResponse<IStepperDataResponse>>(
      `item-guide-assignments/${itemWorkInstructionAssignmentId}`,
      {
        params: httpParams,
      },
    );
  }

  private updateOne(
    submissionId: number,
    dto: IItemAssignedStepUpdateDto,
  ): Observable<IBaseResponse<IWorkInstructionStep>> {
    return this.http.patch<IBaseResponse<IWorkInstructionStep>>(`guide-submission/${submissionId}`, dto);
  }

  private createOne(dto: IItemAssignedStepCreateDto): Observable<IBaseResponse<IWorkInstructionStep>> {
    return this.http.post<IBaseResponse<IWorkInstructionStep>>('guide-submission', dto);
  }
}
